// Popover content for Signal Detection fields

export const firstPersonPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Does the paragraph include informal slang or exclamations with religious
      roots?
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Captures informal or casual religious expressions that may reveal tone,
      mockery, or cultural attitude not obvious through formal religious terms,
      helping inform fields like tone, disrespect-towards-religion?, and
      final-bias-stance-by-religion.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <strong>Yes:</strong> After he finally fixed the projector, someone in the
      back shouted, “Hallelujah!”
      <br />
      <strong>No:</strong> The students discussed the historical significance of
      early Christian rituals.
    </div>
  </div>
);

export const physicalContactPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of the formal religion‑related words that were spotted.
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Provides transparency into what triggered religious detection and supports
      highlighting, auditing, or debugging of model decisions.
    </div>
    <div>
      <strong>Example Content:</strong>

      <div style={{ marginTop: 8 }}>
        The Bible is considered a sacred text in Christianity and has influenced
        Western literature and law. The prophet Elijah is a key figure in both
        Jewish and Christian traditions, known for his confrontation with the
        prophets of Baal.
      </div>
    </div>
  </div>
);

export const settingPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Which religions are explicitly named?
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Ensures downstream fields like final-bias-stance-by-religion and
      disrespect-towards-religion? can be accurately applied to the correct
      faiths mentioned in the text.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        While Christianity emphasizes salvation through faith, Islam teaches
        submission to the will of God as the path to peace.
      </div>
    </div>
  </div>
);

export const explicitTermsFoundPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Are “I /we /you” used, which might signal advocacy or preaching?
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Helps flag passages that speak directly to the reader or align the
      narrator with a belief, which can signal persuasive or devotional intent.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "You should always have faith, even in difficult
        times."
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "Believers often gather to pray during times of
        hardship. "
      </div>
    </div>
  </div>
);

export const explicitTermsListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of detected explicit terms
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Lets reviewers or later models see exactly what terms were used, aiding in
      transparency and context-based override decisions.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />-
    </div>
  </div>
);

export const anatomicalTermsPresentPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Looks for body-part references
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Surfaces content that includes potentially sensitive anatomical references
      that aid context-based review
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "The diagram labeled parts of the female anatomy,
        including the br**sts"
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "The lungs are responsible for oxygen exchange
        in the bloodstream."
      </div>
    </div>
  </div>
);

export const anatomicalTermsListPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Body-related terms detected
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Clarifies what anatomical references were made
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />-
    </div>
  </div>
);

export const containsDialoguePopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      Checks for spoken interaction
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting dialogue helps the pipeline distinguish between internal
      narration and direct character interaction, which is critical for
      identifying scenes where inappropriate behavior is communicated through
      spoken words.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        <strong>True:</strong> "She looked up and whispered, 'Do you think
        anyone saw us?'"
      </div>
      <div style={{ marginTop: 8 }}>
        <strong>False:</strong> "He walked past her in silence, their eyes
        meeting briefly before he turned away."
      </div>
    </div>
  </div>
);

export const chromosomallyDefinedGenderPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of genders of persons explicitly described and identified
      chromosomally
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Detecting the gender of characters helps the system evaluate relationship
      dynamics and scene context.
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "He had his shirt off at the festival and was waving his arms in the
        air"
        <br />→ Output: male
      </div>
    </div>
  </div>
);

export const selfIdentifiedGendersPopoverContent = (
  <div
    style={{
      maxWidth: 350,
      background: '#f5f5f5',
      padding: 16,
      borderRadius: 8,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ marginBottom: 16 }}>
      <strong>Description:</strong>
      <br />
      List of self identified genders mentioned, even if chromosomally defined
      as well or implied
    </div>
    <div style={{ marginBottom: 16 }}>
      <strong>Why This Matters:</strong>
      <br />
      Adds nuance, good to know...
    </div>
    <div>
      <strong>Example Content:</strong>
      <br />
      <div>
        "Some days I feel more like a boy, other days more like a girl — and
        sometimes, neither really fits. I don’t expect everyone to get it, but
        this is who I am."
        <br />→ Output: genderfluid
      </div>
    </div>
  </div>
);
